import { useEffect, useState } from 'react';
import { User } from 'lucide-react';
import { apiUrl, authHeaders, getStoredAuthToken } from '../lib/api';

function initials(displayName: string | null | undefined, email: string | null | undefined): string {
  const s = (displayName || '').trim();
  if (s.length >= 2) {
    const parts = s.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return s.slice(0, 2).toUpperCase();
  }
  const e = (email || '').trim();
  if (e.length >= 2) return e.slice(0, 2).toUpperCase();
  return '?';
}

interface UserAvatarProps {
  displayName?: string | null;
  email?: string | null;
  hasAvatar?: boolean;
  size?: number;
  className?: string;
  imageVersion?: number;
}

/**
 * Laadt profielfoto via GET /me/avatar (Bearer); anders initialen.
 */
export function UserAvatar({
  displayName,
  email,
  hasAvatar,
  size = 36,
  className = '',
  imageVersion = 0,
}: UserAvatarProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!hasAvatar || !getStoredAuthToken()) {
      setBlobUrl(null);
      return;
    }
    const ctrl = new AbortController();
    let created: string | null = null;
    (async () => {
      try {
        const res = await fetch(apiUrl('/me/avatar'), {
          headers: authHeaders(),
          credentials: 'include',
          signal: ctrl.signal,
        });
        if (res.status === 204 || !res.ok) return;
        const blob = await res.blob();
        if (blob.size === 0) return;
        created = URL.createObjectURL(blob);
        setBlobUrl(created);
      } catch {
        /* abort of netwerk */
      }
    })();
    return () => {
      ctrl.abort();
      if (created) URL.revokeObjectURL(created);
    };
  }, [hasAvatar, imageVersion]);

  const ini = initials(displayName, email);
  const nameForAlt = displayName?.trim() || email?.trim() || '';
  const photoAlt = nameForAlt ? `Profielfoto van ${nameForAlt}` : 'Profielfoto';

  if (blobUrl) {
    return (
      <img
        src={blobUrl}
        alt={photoAlt}
        width={size}
        height={size}
        className={`rounded-full object-cover ring-2 ring-border/80 ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-center rounded-full bg-primary/15 font-semibold text-primary ring-2 ring-border/60 ${className}`}
      style={{ width: size, height: size, fontSize: Math.max(11, size * 0.32) }}
      aria-hidden
    >
      {ini === '?' ? <User className="h-[45%] w-[45%] opacity-80" /> : ini}
    </span>
  );
}
